import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";
import Moment from "react-moment";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";

import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import CommonLayout from "../../Common/Layout";
import KMKTable from "../../Common/Table/KMKTable";
import SageMakerService from "../../../service/sageMakerService";
import { ButtonUI, ConfirmPopup } from "../../Common";
import { showToast } from "../../../store/actions";
import { convertTimezone, setPrevState } from "../../../util/common";
import MultiSelectDropDown from "../../Common/MultiSelectDropDown";
import UpdateImagePanel from "./UpdateImagePanel";
import MultiSelect from "../../../util/multiselect";
import useWindowResize from "../../../hooks/useWindowsReszie";
import Toolbar from "../../Common/Toolbar";

const useStyles = makeStyles()((theme) => ({
  PanelContainer: {
    padding: "10px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
      wordBreak: "break-word",
    },
    "& .MuiTablePagination-toolbar": {
      minHeight: "40px",
    },
    "& P.MuiTablePagination-selectLabel,P.MuiTablePagination-displayedRows": {
      marginBottom: "0px !important",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "35px",
      marginLeft: "-2px",
    },
  },
}));

const CustomPaper = (props) => {
  return (
    <Paper
      {...props}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxWidth: "100%",
          wordBreak: "break-all !important",
        },
      }}
    />
  );
};

const statusList = [
  { label: "Stopped", Name: "Stopped" },
  { label: "Stopping", Name: "Stopping" },
  { label: "Starting", Name: "Starting" },
  { label: "Failed", Name: "Failed" },
  { label: "Running", Name: "Running" },
];
const defaultStatusFilter = ["Stopping", "Starting", "Failed", "Running"];

const updateAllowed = (status) => ["Stopped", "Failed"].includes(status);

const SageMakerUsage = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { height: tableHeight } = useWindowResize(200);

  const [loading, setLoading] = useState(false);
  const [loadingForConfirm, setLoadingForConfirm] = useState(false);

  const [sageMakerData, setSageMakerData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(defaultStatusFilter);
  const [selected, setSelected] = useState([]);
  const [updateImageDrawer, setUpdateImageDrawer] = useState(false);

  const [reportState, setReportState] = useState({
    orderBy: "SpaceName",
    order: "asc",
    selectedUsers: [],
  });

  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
    message: "",
    record: {},
  });

  const { orderBy, order, selectedUsers } = reportState;

  const user = useSelector((state) => state.user);
  const isSysAdmin = useMemo(() => user.currentUser.Roles.includes("SysAdmin"), [user.currentUser.Roles]);

  const handleCancel = useCallback(() => {
    setConfirmPopup({ open: false, title: "", message: "", loading: false, record: {} });
  }, []);

  const getUsageReport = useCallback(async () => {
    setLoading(true);
    setSageMakerData([]);
    const sageMaker = new SageMakerService();
    const { success, data } = await sageMaker.getUsageReport();
    if (success) setSageMakerData(data);
    setLoading(false);
  }, []);

  const confirmClick = useCallback(
    async (type, record) => {
      setLoadingForConfirm(true);
      const submitData = {
        DomainId: record?.DomainId,
        SpaceName: record?.SpaceName,
        ...(type === 1 ? { AppType: record?.Application, AppName: record?.AppName } : {}),
      };

      const sageMaker = new SageMakerService();
      const { success, message } =
        type === 1 ? await sageMaker.stopRunningInstance(submitData) : await sageMaker.deleteSpace(submitData);

      if (success) {
        dispatch(showToast({ message: message, type: "success" }));

        //removing deleted space from selection
        if (type === 2) setSelected((prev) => prev.filter((spaceName) => spaceName !== record?.SpaceName));
        getUsageReport();
      }

      handleCancel();
      setLoadingForConfirm(false);
    },
    [dispatch, getUsageReport, handleCancel]
  );

  const handleEmptyValue = useCallback((value) => value === "-" || value === "", []);

  const handleSorting = useCallback((property) => {
    setReportState((prev) => {
      const isAsc = prev.orderBy === property && prev.order === "asc";
      return {
        orderBy: property,
        order: isAsc ? "desc" : "asc",
        selectedUsers: prev.selectedUsers,
      };
    });
  }, []);

  // Filter data as per search value then sorting will be applied
  const $filteredData = useMemo(() => {
    const data =
      (selectedUsers?.length === 0
        ? sageMakerData
        : sageMakerData.filter(({ UserId }) => selectedUsers.includes(UserId))) || [];

    return data.filter(({ Status }) => selectedStatus.includes(Status));
  }, [selectedUsers, sageMakerData, selectedStatus]);

  // Sorting data as per the selection applied in the table header
  const $sortedData = useMemo(
    () =>
      ($filteredData || []).sort((a, b) => {
        if (orderBy === "CreatedTime") {
          const _aValue = handleEmptyValue(a[orderBy]) ? new Date(0) : new Date(a[orderBy]);
          const _bValue = handleEmptyValue(b[orderBy]) ? new Date(0) : new Date(b[orderBy]);

          return order === "asc" ? _aValue - _bValue : _bValue - _aValue;
        } else if (orderBy === "RunningTime") {
          const _aValue = handleEmptyValue(a[orderBy]) ? new Date() : new Date(a[orderBy]);
          const _bValue = handleEmptyValue(b[orderBy]) ? new Date() : new Date(b[orderBy]);

          return order === "asc" ? _bValue - _aValue : _aValue - _bValue;
        } else return order === "asc" ? a[orderBy]?.localeCompare(b[orderBy]) : b[orderBy]?.localeCompare(a[orderBy]);
      }),
    [$filteredData, orderBy, order, handleEmptyValue]
  );

  //Every Stopped instance in current selection is selected or not
  const isAllSelected = useMemo(
    () =>
      !!(
        $sortedData.length &&
        $sortedData.filter((row) => updateAllowed(row.Status)).every(({ SpaceName }) => selected.includes(SpaceName))
      ),
    [$sortedData, selected]
  );

  //Any current selection is selectable or not
  const disableSelectAll = useMemo(
    () => !$sortedData.length || !isSysAdmin || $sortedData.every((row) => !updateAllowed(row.Status)),
    [$sortedData, isSysAdmin]
  );

  //Select all toggle
  //Selecting all Stopped
  const handleToggleSelectAll = useCallback(() => {
    if (isAllSelected) setSelected([]);
    else
      setSelected((prev) => {
        const _selected = new Set([
          ...prev,
          ...$sortedData.filter((row) => updateAllowed(row.Status)).map((row) => row.SpaceName),
        ]);
        return [..._selected];
      });
  }, [$sortedData, isAllSelected]);

  //Row checkbox toggle
  const handleToggleSelect = useCallback((SpaceName) => {
    setSelected((prevSelected) => {
      if (prevSelected.includes(SpaceName)) return prevSelected.filter((_selectedId) => _selectedId !== SpaceName);
      else return [...prevSelected, SpaceName];
    });
  }, []);

  const reportColumns = useMemo(
    () => [
      {
        id: "Checkbox",
        name: "",
        sortable: false,
        style: { maxWidth: 50 },
        align: "center",
        width: 50,
        renderHeadCell: () => (
          <Checkbox disabled={disableSelectAll} checked={isAllSelected} onChange={handleToggleSelectAll} />
        ),
        renderValueCell: (val, record, index) =>
          updateAllowed(record.Status) ? (
            <Checkbox
              checked={selected.includes(record.SpaceName)}
              onChange={() => handleToggleSelect(record.SpaceName)}
              disabled={!isSysAdmin}
            />
          ) : (
            <></>
          ),
      },
      {
        id: "SpaceName",
        name: "SPACE NAME",
        sortable: true,
        align: "left",
        width: 160,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "UserId",
        name: "USER ID",
        sortable: true,
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "SpaceType",
        name: "SPACE TYPE",
        sortable: true,
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Application",
        name: "APPLICATION",
        sortable: true,
        align: "left",
        width: 130,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Instance",
        name: "INSTANCE",
        sortable: true,
        align: "left",
        width: 130,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "ImageName",
        name: "IMAGE",
        sortable: true,
        align: "left",
        width: 140,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (
          <Tooltip placement="right" title={record.ImageArn}>
            {val || "-"}
          </Tooltip>
        ),
      },
      {
        id: "Storage",
        name: "STORAGE",
        sortable: true,
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "Status",
        name: "STATUS",
        sortable: true,
        align: "left",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => val || "-",
      },
      {
        id: "CreatedTime",
        name: "CREATED TIME",
        sortable: true,
        align: "center",
        width: 180,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => (val ? convertTimezone(val) : "-"),
      },
      {
        id: "RunningTime",
        name: "RUNNING TIME",
        sortable: true,
        align: "center",
        width: 160,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) =>
          val ? (
            <Moment fromNow ago utc>
              {val}
            </Moment>
          ) : (
            "-"
          ),
      },
      {
        id: "ACTION",
        name: "ACTIONS",
        sortable: false,
        align: "right",
        width: 120,
        renderHeadCell: (name) => name,
        renderValueCell: (val, record, index) => {
          if (!isSysAdmin) return <></>;
          return (
            <Box sx={{ p: 0, m: 0, display: "flex", alignItems: "space-around", justifyContent: "flex-end" }}>
              {record?.Status === "Running" && (
                <Tooltip title="Stop" arrow>
                  <IconButton
                    className={classes.IconHolder}
                    onClick={() =>
                      setConfirmPopup({
                        open: true,
                        title: "Stop Instance",
                        message: `Are you sure you want to stop the SageMaker instance of space "${record?.SpaceName}"?`,
                        record,
                        type: 1,
                      })
                    }
                    sx={{ mr: 1 }}
                  >
                    <StopCircleOutlinedIcon fontSize="inherit" color="error" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Delete" arrow>
                <IconButton
                  className={classes.IconHolder}
                  onClick={() =>
                    setConfirmPopup({
                      open: true,
                      title: "Delete Space",
                      message: `Are you sure you want to delete space “${record?.SpaceName}”?`,
                      record,
                      type: 2,
                    })
                  }
                >
                  <DeleteOutlineOutlinedIcon fontSize="inherit" color="error" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      classes.IconHolder,
      disableSelectAll,
      handleToggleSelect,
      handleToggleSelectAll,
      isAllSelected,
      selected,
      isSysAdmin,
    ]
  );

  useEffect(() => {
    getUsageReport();
    return () => {};
  }, [getUsageReport]);

  const allUserIds = useMemo(() => {
    return [
      ...new Set(
        (sageMakerData ?? []).reduce((acc, { UserId }) => {
          if (UserId && UserId !== "") acc.push(UserId);
          return acc;
        }, [])
      ),
    ];
  }, [sageMakerData]);

  const breadcrumbArr = useMemo(
    () => [
      { link: "/reports", displayName: "Reports", active: false },
      { link: "#", displayName: "SageMaker Usage", active: true },
    ],
    []
  );

  const handleStatusChange = (selectionList) => setSelectedStatus(selectionList);

  const handleClose = useCallback(
    (refreshData = false) => {
      setUpdateImageDrawer(false);
      if (refreshData === true) {
        setSelected([]);
        getUsageReport();
      }
    },
    [getUsageReport]
  );

  return (
    <CommonLayout title="SageMaker Usage" breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ m: 0 }} className={classes.PanelContainer}>
        <Toolbar title="SageMaker Usage" helpText="SageMaker Usage">
          <MultiSelectDropDown
            dataid="id"
            variant="standard"
            placeHolder={<MultiSelect title="Status" dataLength={selectedStatus.length} />}
            options={statusList}
            selected={selectedStatus}
            isSelectAll={true}
            disabled={loading}
            onSelectChange={handleStatusChange}
            basedOn="Name"
            allowSearching
          />
          <Box sx={{ mr: 2 }}>
            <Autocomplete
              multiple
              fullWidth
              PaperComponent={CustomPaper}
              className={classes.AutoComplete}
              sx={{ mr: "20px", minWidth: 220 }}
              size="small"
              options={allUserIds}
              value={reportState.selectedUsers}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(option) => option}
              onChange={(e, selectedObj) =>
                setPrevState(setReportState, { selectedUsers: selectedObj.length ? [...selectedObj] : [] })
              }
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              renderTags={(selected) => {
                return selected.length <= 1 ? (
                  <Chip label={selected[0]} size="small" className={classes.SelectChip} />
                ) : (
                  <>
                    <Chip label={selected[0]} size="small" className={classes.SelectChip} />
                    <p style={{ fontSize: "0.8rem", margin: "4px 0 0 7px" }}>{`+ ${selected.length - 1}`}</p>
                  </>
                );
              }}
              renderInput={(params) => <TextField fullWidth variant="outlined" {...params} label="User IDs" />}
              disabled={loading || loadingForConfirm}
            />
          </Box>
          {isSysAdmin && (
            <ButtonUI
              variant="contained"
              color="info"
              style={{ width: "140px", marginRight: "10px" }}
              onClick={() => setUpdateImageDrawer(true)}
              disabled={!selected.length}
            >
              Update Image {selected.length ? `(${selected.length})` : ""}
            </ButtonUI>
          )}
          <Box>
            <Tooltip placement="top" title="Refresh" arrow>
              <span>
                <IconButton disabled={loading || loadingForConfirm} onClick={getUsageReport}>
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Toolbar>

        <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
          <KMKTable
            loading={loading}
            columns={reportColumns}
            containerHeight={tableHeight}
            noDataText={"No records found"}
            data={$sortedData}
            sortingProperties={{ order, orderBy, handleSorting }}
            boldHeader
            tableProps={{ stickyHeader: true }}
            paginationProperties={{}}
            dataIdKey="SageMakerReportID"
          />
        </Paper>
      </Container>
      {confirmPopup.open && (
        <ConfirmPopup
          configData={{
            popupTitle: confirmPopup.title,
            popupMessage: <Box sx={{ maxWidth: "400px" }}>{confirmPopup.message}</Box>,
            handleCancel,
            handleClick: () => confirmClick(confirmPopup.type, confirmPopup.record),
            loadingForConfirm,
            confirmBtnColor: "error",
            confirmBtnName: confirmPopup.type === 1 ? "Stop" : "Delete",
          }}
        />
      )}

      {updateImageDrawer && <UpdateImagePanel selected={selected} handleClose={handleClose} />}
    </CommonLayout>
  );
};

export default SageMakerUsage;
